import { Inject, Injectable } from '@angular/core';
import { IDatabaseService } from 'src/app/shared/services/contracts/database/database-service';
import { DatabaseService } from 'src/app/shared/services/database.service';

interface SentryThrottledErrorState {
  _id?: string;
  count: number;
  lastSentAt?: string; // ISO string
}

/**
 * Throttles noisy Sentry errors.
 * Add patterns in `patterns`. Sends first and every Nth occurrence.
 */
@Injectable({ providedIn: 'root' })
export class SentryThrottleService {
  private readonly dbName = 'statePersister.db';

  constructor(@Inject(DatabaseService) private readonly dbService: IDatabaseService) {}

  /** Pattern registry. Add more entries to throttle additional errors. */
  private readonly patterns: Array<{
    id: string; // used to build extra payload key
    test: RegExp; // regex against error message
    key: string; // storage key suffix (sentry: prefix is added automatically)
    threshold: number; // send on first and every Nth occurrence
    tags?: Record<string, string>; // tags to add when sending
  }> = [
    {
      id: 'idbDisconnect',
      test: /indexed database server.*lost/i,
      key: 'idbDisconnect',
      threshold: 100,
      tags: { context: 'idb-issue' },
    },
    {
      // a data call answered with 401 (#27294): counted instead of ignored, so it stays measurable
      id: 'unauthenticatedCall',
      test: /^Not authenticated$/i,
      key: 'unauthenticatedCall',
      threshold: 25,
      tags: { context: 'unauthenticated-call' },
    },
    {
      id: 'missingControlName',
      test: /Cannot find control with unspecified name attribute/i,
      key: 'missingControlName',
      threshold: 250,
      tags: { context: 'unspecified-name' },
    },
  ];

  /** Sentry beforeSend: match pattern, throttle, enrich, return event or null. */
  public async beforeSend(event: any, hint?: { originalException?: any }): Promise<any | null> {
    try {
      const value = (hint?.originalException as Error)?.message || event?.exception?.values?.[0]?.value || '';
      const message = String(value);

      const pattern = this.patterns.find(p => p.test.test(message));
      if (!pattern) {
        return event; // not a throttled pattern, handle as usual
      }

      const { shouldSend, state, threshold } = await this.incrementAndDecide(
        `sentry:${pattern.key}`,
        pattern.threshold
      );
      if (!shouldSend) return null;

      const lastOccurrenceAt = new Date().toISOString();

      event.extra = {
        ...(event.extra || {}),
        [pattern.id]: {
          count: state.count,
          threshold: threshold,
          lastSentAt: state.lastSentAt,
          lastOccurrenceAt: lastOccurrenceAt,
        },
      };
      event.tags = {
        ...(event.tags || {}),
        ...pattern.tags,
      };
      return event;
    } catch (e) {
      window.logger?.error?.('SentryThrottleService.beforeSend error', e);
      // In case of error in throttle, drop to avoid noise
      return null;
    }
  }

  /** Increment counter for key and decide if we should send (1st or every Nth). */
  private async incrementAndDecide(
    key: string,
    threshold: number
  ): Promise<{ shouldSend: boolean; state: SentryThrottledErrorState; threshold: number }> {
    try {
      const storage = await this.dbService.getDatabase(this.dbName);
      const current: SentryThrottledErrorState = (await storage.getItem(key)) || { count: 0 };
      const next: SentryThrottledErrorState = { ...current, _id: key, count: (current.count || 0) + 1 };

      const isFirst = next.count === 1;
      const isThreshold = next.count % threshold === 0;
      const shouldSend = isFirst || isThreshold;

      if (shouldSend) {
        next.lastSentAt = new Date().toISOString();
      }

      await storage.setItem(key, next);
      return { shouldSend, state: next, threshold };
    } catch (error) {
      window.logger?.error?.(`SentryThrottleService error ${key}`, error);
      return { shouldSend: false, state: { count: 0 }, threshold };
    }
  }
}
